import React, { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import classNames from 'classnames';
import { ChatType } from 'lavva.exalushome/build/js/Services/Chat/IChatService';
import { IconMicrophone, IconSendMessage } from '../../../../assets/icons';
import { Header, Page, Tabs, TextArea } from '../../../../components';
import { AvailableLanguage } from '../../../../types';
import { scrollBottom } from '../../../../utils/helpers/scroll';
import { ChatList } from './components/chat-list';
import { useControllerChat } from './hooks/use-controller-chat';
import { useFamilyChat } from './hooks/use-family-chat';
import './index.scss';

const Chat: React.FC = () => {
  const { i18n, t } = useTranslation('chat');
  const homeChatAvailable = i18n.language === AvailableLanguage.PL;
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [message, setMessage] = useState<string>('');
  const { controllerChat, sendControllerMessage } = useControllerChat();
  const { familyChat, sendFamilyMessage } = useFamilyChat();
  const { transcript, listening, browserSupportsSpeechRecognition } = useSpeechRecognition();

  useEffect(() => {
    return () => SpeechRecognition.stopListening();
  }, []);

  useEffect(() => scrollBottom(), [activeTabIndex]);

  useEffect(() => {
    setMessage(transcript);
  }, [transcript]);

  const handleTabChange = (tabIndex: number) => setActiveTabIndex(tabIndex);

  const tabs = [...(homeChatAvailable ? [{ label: t('home') }] : []), { label: t('family') }];

  const handleSetValue = (e: React.ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value);

  const handleKeyboardEvent = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter') handleSendMessage();
  };

  const handleMicrophoneClick = () => {
    if (!listening) SpeechRecognition.startListening({ language: i18n.language });
    else SpeechRecognition.stopListening();
  };

  const handleSendMessage = () => {
    if (message.trim()) {
      switch (chatType) {
        case ChatType.ControllerChat:
          sendControllerMessage(message, setMessage, ChatType.ControllerChat);
          break;
        case ChatType.FamilyChat:
          sendFamilyMessage(message, setMessage, ChatType.FamilyChat);
          break;
        default:
          break;
      }
    }
  };

  const chatType = useMemo(() => {
    if (homeChatAvailable) return activeTabIndex as ChatType;
    return ChatType.FamilyChat;
  }, [activeTabIndex, homeChatAvailable]);

  const displayingChat = useMemo(() => {
    switch (chatType) {
      case ChatType.ControllerChat:
        return controllerChat;
      case ChatType.FamilyChat:
        return familyChat;
      default:
        return [];
    }
  }, [chatType, controllerChat, familyChat]);

  return (
    <Page
      className="chat-page"
      isStickyHeader
      containerClassName="page__container--flex-height page__container--no-padding"
      header={
        <>
          <Header title={t('chat')}>
            <Tabs
              tabList={tabs}
              activeTabIndex={activeTabIndex}
              setActiveTabIndex={handleTabChange}
              tabsType="pills"
              isDivider={false}
            />
          </Header>
        </>
      }
    >
      <>
        <div className={classNames('chat-view max-width-desktop', { empty: !displayingChat.length })}>
          <ChatList chat={displayingChat} chatType={chatType} />
        </div>
        <div className="row-container max-width-desktop">
          <TextArea
            defaultValue={message}
            value={message}
            label={''}
            placeholder={t('writeMessage')}
            onChange={handleSetValue}
            onKeyDown={handleKeyboardEvent}
          />
          {listening ? (
            <button onClick={handleSendMessage} className={classNames('action-btn', { active: true })}>
              <IconMicrophone active />
            </button>
          ) : message || !browserSupportsSpeechRecognition ? (
            <button onClick={handleSendMessage} className="action-btn">
              <IconSendMessage />
            </button>
          ) : (
            <button onClick={handleMicrophoneClick} className="action-btn">
              <IconMicrophone />
            </button>
          )}
        </div>
      </>
    </Page>
  );
};

export default Chat;
